import { forwardRef, useState, useEffect } from 'react';
import { PurchaseInvoiceDocumentRecord, CompanySettings, DEFAULT_COMPANY_SETTINGS, getCompanySettings } from '@/services/firestore';

interface PurchaseInvoiceDocumentProps {
    data: PurchaseInvoiceDocumentRecord;
    company?: CompanySettings;
}

export const PurchaseInvoiceDocument = forwardRef<HTMLDivElement, PurchaseInvoiceDocumentProps>(({ data, company: propCompany }, ref) => {
    const [company, setCompany] = useState<CompanySettings>(propCompany || DEFAULT_COMPANY_SETTINGS);

    useEffect(() => {
        if (propCompany) {
            setCompany(propCompany);
        } else {
            getCompanySettings().then(setCompany).catch(() => {});
        }
    }, [propCompany]);

    return (
        <div ref={ref} className="bg-white p-8 w-[210mm] min-h-[297mm] mx-auto text-black font-sans relative" style={{ boxSizing: 'border-box' }}>
            {/* Header / Logo Section */}
            <div className="flex items-center justify-center border-b-[3px] border-b-black pb-2 mb-8 relative">
                {/* Logo Placeholder */}
                <div className="absolute left-0 top-0">
                    <img src="/logo.png" alt="Logo" className="h-16 object-contain" onError={(e: any) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.innerHTML = '<div class="h-16 w-32 border-2 border-black flex items-center justify-center font-bold text-2xl italic">POS</div>'; }} />
                </div>
                <div className="text-center ml-24">
                    <h1 className="text-4xl font-black italic tracking-widest text-black m-0 leading-none">{company.companyName}</h1>
                    {company.tagline && (
                        <p className="font-semibold text-[13px] tracking-[0.2em] uppercase mt-1">{company.tagline}</p>
                    )}
                    <p className="text-[12px] font-bold mt-1">{company.address}{company.city ? `, ${company.city}` : ''}</p>
                </div>
            </div>

            {/* Top Info Section: Attn, Address, Date */}
            <div className="flex justify-between items-start mb-8 text-[14px]">
                <div className="leading-[1.6]">
                    {data.attnName1 && (
                        <div><strong>Attn :R / Mr,</strong> {data.attnName1} {data.attnRole1 ? `(${data.attnRole1})` : ''}</div>
                    )}
                    {data.attnName2 && (
                        <div className="pl-24">{data.attnName2} {data.attnRole2 ? `(${data.attnRole2})` : ''}</div>
                    )}
                    <div className="mt-1 font-bold">Supplier: {data.supplierName || data.companyName}</div>
                    <div>{data.companyAddress}</div>
                </div>
                <div className="border-[2px] border-black px-4 py-1 font-bold self-start mt-2">
                    Date {data.date}
                </div>
            </div>

            {/* Subject Line */}
            {data.subject && (
                <div className="text-center mb-8">
                    <span className="font-bold underline text-[15px] underline-offset-4 decoration-2">
                        {data.subject}
                    </span>
                </div>
            )}

            {/* Sub-header */}
            <div className="mb-4 text-[14px] font-semibold">
                Purchase Order / Supply Delivery Record:
            </div>

            {/* Items Table - Heavy borders matching image */}
            <table className="w-full border-collapse border-[2px] border-black text-[13px] mb-12">
                <thead>
                    <tr className="border-b-[2px] border-black bg-[#e6e6e6]">
                        <th className="border-[2px] border-black px-2 py-1.5 w-12 text-center font-bold">S.No</th>
                        <th className="border-[2px] border-black px-2 py-1.5 text-center font-bold">Item Detail & Specs</th>
                        <th className="border-[2px] border-black px-2 py-1.5 w-24 text-center font-bold">Brand</th>
                        <th className="border-[2px] border-black px-2 py-1.5 w-32 text-center font-bold">Item Code</th>
                        <th className="border-[2px] border-black px-2 py-1.5 w-24 text-center font-bold">Unit Rate</th>
                        <th className="border-[2px] border-black px-2 py-1.5 w-24 text-center font-bold">Qty Req</th>
                        <th className="border-[2px] border-black px-2 py-1.5 w-32 text-center font-bold">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {data.items.map((item, index) => (
                        <tr key={index} className="border-b-[2px] border-black">
                            <td className="border-[2px] border-black px-2 py-2 text-center font-bold text-[14px]">{item.sNo || index + 1}</td>
                            <td className="border-[2px] border-black px-3 py-2 uppercase leading-snug break-words max-w-[200px]">{item.description || item.name}</td>
                            <td className="border-[2px] border-black px-2 py-2 text-center">{item.brand || '-'}</td>
                            <td className="border-[2px] border-black px-2 py-2 text-center">{item.itemCode || item.code}</td>
                            <td className="border-[2px] border-black px-2 py-2 text-center font-bold">{item.unitRate || item.price}</td>
                            <td className="border-[2px] border-black px-2 py-2 text-center font-bold">{item.quantity}</td>
                            <td className="border-[2px] border-black px-2 py-2 text-center font-bold">{item.total}</td>
                        </tr>
                    ))}
                    {data.items.length === 0 && (
                        <tr>
                            <td colSpan={7} className="border-[2px] border-black p-4 text-center text-gray-400">No items added to this invoice</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Footer Signatures */}
            <div className="absolute bottom-16 left-8 right-8 text-[13px]">
                <div className="mb-2 font-bold ml-4">Taj Muhammad Salik ( BDM )</div>
                <div className="font-bold italic ml-4 mb-2">Dynamic Industrial Solutions</div>
                
                {/* Signature line / stamp area */}
                <div className="relative h-32 w-full mt-4 flex items-start">
                    {/* Placeholder for real signature image path if provided later */}
                    <img src="/stamp.png" alt="" className="h-28 ml-48 object-contain mix-blend-multiply opacity-80" onError={(e: any) => { e.currentTarget.style.display = 'none'; }} />
                    <img src="/signature.png" alt="" className="h-24 absolute top-2 left-6 object-contain mix-blend-multiply" onError={(e: any) => { e.currentTarget.style.display = 'none'; }}/>
                    
                    {/* Fallback drawn line if images missing */}
                    <div className="absolute top-12 left-4 w-48 border-b-2 border-black/30"></div>
                </div>
            </div>
            
            <style>
                {`
                    @media print {
                        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; background: transparent; }
                        @page { size: A4; margin: 0; }
                    }
                `}
            </style>
        </div>
    );
});
PurchaseInvoiceDocument.displayName = 'PurchaseInvoiceDocument';
